/**
 * Parse subscription error messages and provide user-friendly messages and actions
 * @param {string} errorMessage - The error message from the API
 * @returns {Object} Error details with type, action, and user-friendly message
 */
export function parseSubscriptionError(errorMessage) {
  if (!errorMessage) {
    return {
      type: 'unknown',
      action: 'dismiss',
      message: errorMessage,
      userFriendlyMessage: 'An error occurred. Please try again or contact support.'
    };
  }

  const errorMap = {
    'A payment is currently being processed': {
      type: 'payment_processing',
      action: 'retry',
      message: errorMessage,
      userFriendlyMessage: 'A payment is being processed. Please wait a moment and try again.'
    },
    'Cannot change subscription while it is in RETRYING status': {
      type: 'payment_retrying',
      action: 'update_payment',
      message: errorMessage,
      userFriendlyMessage: 'Your last payment failed. Please update your payment method before making changes.'
    },
    'Cannot change subscription while it is in SUSPENDED status': {
      type: 'subscription_suspended',
      action: 'contact_support',
      message: errorMessage,
      userFriendlyMessage: 'Your subscription is suspended. Please contact support or update your payment method.'
    },
    'You are already subscribed to this plan with the same billing interval': {
      type: 'no_change_needed',
      action: 'dismiss',
      message: errorMessage,
      userFriendlyMessage: 'You already have this plan with this billing cycle.'
    },
    'Cannot change to a plan from a different country': {
      type: 'country_mismatch',
      action: 'cancel_first',
      message: errorMessage,
      userFriendlyMessage: 'To switch to a plan from another country, please cancel your current subscription first.'
    },
    'Cannot downgrade to free tier': {
      type: 'free_tier_blocked',
      action: 'cancel_subscription',
      message: errorMessage,
      userFriendlyMessage: 'To switch to the free plan, please cancel your subscription instead of changing plans.'
    },
    'To change billing interval for the same plan': {
      type: 'wrong_endpoint',
      action: 'retry',
      message: errorMessage,
      userFriendlyMessage: 'Please use the billing interval toggle to change your payment frequency.'
    },
    'Please resolve payment issues first': {
      type: 'payment_issues',
      action: 'update_payment',
      message: errorMessage,
      userFriendlyMessage: 'Please update your payment method before making changes.'
    }
  };

  // Find matching error
  for (const [key, error] of Object.entries(errorMap)) {
    if (errorMessage.includes(key)) {
      return error;
    }
  }

  // Default error for unknown cases
  return {
    type: 'unknown',
    action: 'dismiss',
    message: errorMessage,
    userFriendlyMessage: errorMessage || 'An error occurred. Please try again or contact support.'
  };
}

/**
 * Get action button config based on error action type
 * @param {string} action - The action type from parseSubscriptionError
 * @param {function} navigate - React Router navigate function
 * @param {function} openSupportChat - Function to open support chat (optional)
 * @returns {Object|null} Button configuration or null if no action button needed
 */
export function getErrorActionButton(action, navigate, openSupportChat = null) {
  const actionMap = {
    update_payment: {
      text: 'Update Payment Method',
      handler: () => navigate('/billing?tab=0')
    },
    cancel_subscription: {
      text: 'Cancel Subscription',
      handler: () => navigate('/billing?tab=1')
    },
    contact_support: {
      text: 'Contact Support',
      handler: openSupportChat || (() => window.open('mailto:support@playaz4playaz.com', '_blank'))
    },
    cancel_first: {
      text: 'Manage Subscription',
      handler: () => navigate('/billing?tab=1')
    }
  };

  return actionMap[action] || null;
}
